import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Alert, Card, Choice, FormControl} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil} from 'ramda';

import {useGetUsersQuery} from '@dms/api/customer';
import {UserStateFilterSchema} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {getUserName} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SIMULTANEOUS_RING_CAP} from '../../TelephonyGroups/utils/getTelephonyGroupSummary';
import {TelephonyGroupFormValues} from '../utils/telephonyGroupFormSchema';
import {SortableTelephonyGroupMemberRow} from './SortableTelephonyGroupMemberRow';

interface TelephonyGroupMembersCardProps extends RequiredTestIdProps {
  control: FormControl<TelephonyGroupFormValues>;
  formApi: UseFormReturn<TelephonyGroupFormValues>;
  isReadOnly: boolean;
}

export function TelephonyGroupMembersCard(props: TelephonyGroupMembersCardProps) {
  const memberUserIds = useWatch({control: props.control, name: 'memberUserIds'});
  const ringStrategy = useWatch({control: props.control, name: 'ringStrategy'});

  const {data: users, isLoading: areUsersLoading} = useGetUsersQuery({
    stateFilter: UserStateFilterSchema.enum.active,
  });

  const usersById = indexBy((user) => user.id, users ?? []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const isSequential = ringStrategy === 'sequential';
  const membersWithoutPhoneCount = memberUserIds.filter((userId) =>
    isNil(usersById[userId]?.phoneNumber)
  ).length;
  const isOverSimultaneousCap = !isSequential && memberUserIds.length > SIMULTANEOUS_RING_CAP;

  const setMemberUserIds = (userIds: string[]) =>
    props.formApi.setValue('memberUserIds', userIds, {shouldDirty: true});

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (isNil(over) || active.id === over.id) {
      return;
    }

    const from = memberUserIds.indexOf(active.id.toString());
    const to = memberUserIds.indexOf(over.id.toString());

    setMemberUserIds(arrayMove(memberUserIds, from, to));
  };

  const handleAddMember = (userId: string | null) => {
    if (isNil(userId) || memberUserIds.includes(userId)) {
      return;
    }

    setMemberUserIds([...memberUserIds, userId]);
  };

  const handleRemoveMember = (userId: string) =>
    setMemberUserIds(memberUserIds.filter((memberId) => memberId !== userId));

  const addMemberOptions =
    users
      ?.filter((user) => !user.blocked && !memberUserIds.includes(user.id))
      .map((user) => ({
        value: user.id,
        label: getUserName(user) ?? user.email,
      })) ?? [];

  return (
    <Card
      title={i18n.t('page.settings.telephony.membersCardTitle', {count: memberUserIds.length})}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {isSequential
            ? i18n.t('page.settings.telephony.membersOrderHint')
            : i18n.t('page.settings.telephony.membersOrderIgnoredHint')}
        </Text>
        <Show when={isOverSimultaneousCap}>
          <Alert
            variant="warning"
            message={i18n.t('page.settings.telephony.membersSimultaneousCapWarning', {
              count: SIMULTANEOUS_RING_CAP,
            })}
            data-testid={suffixTestId('capWarning', props)}
          />
        </Show>
        <Show when={membersWithoutPhoneCount > 0}>
          <Alert
            variant="info"
            message={i18n.t('page.settings.telephony.membersNoPhoneWarning', {
              count: membersWithoutPhoneCount,
            })}
            data-testid={suffixTestId('noPhoneWarning', props)}
          />
        </Show>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={memberUserIds} strategy={verticalListSortingStrategy}>
            <VStack spacing={2}>
              {memberUserIds.map((userId, index) => (
                <SortableTelephonyGroupMemberRow
                  key={userId}
                  userId={userId}
                  user={usersById[userId]}
                  position={index + 1}
                  isPositionMeaningful={isSequential}
                  isBeyondSimultaneousCap={!isSequential && index >= SIMULTANEOUS_RING_CAP}
                  isReadOnly={props.isReadOnly}
                  onRemove={handleRemoveMember}
                  data-testid={suffixTestId(`member-[${index}]`, props)}
                />
              ))}
            </VStack>
          </SortableContext>
        </DndContext>
        <Show when={memberUserIds.length === 0}>
          <Text size="small" color="tertiary" align="center">
            {i18n.t('page.settings.telephony.membersEmpty')}
          </Text>
        </Show>
        <Show when={!props.isReadOnly}>
          <Choice<string>
            value={null}
            options={addMemberOptions}
            onChange={handleAddMember}
            isLoading={areUsersLoading}
            placeholder={i18n.t('page.settings.telephony.addMember')}
            data-testid={suffixTestId('addMember', props)}
          />
        </Show>
      </VStack>
    </Card>
  );
}
